import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ConfirmDialog } from "../feed/FeedDialogs";

/**
 * "Leave without saving?" for pages that save from a bar rather than as you
 * go. The app runs on <BrowserRouter>, not a data router, so react-router's
 * useBlocker isn't available: instead the navigation the shell owns (the
 * sidebar) asks first through useGuardedNavigation, and the browser's own
 * prompt covers a reload or a closed tab.
 */
interface LeaveGuardValue {
  setUnsaved: (key: string, message: string | null) => void;
  guarded: (go: () => void) => void;
}

const LeaveGuardContext = createContext<LeaveGuardValue | null>(null);

export function LeaveGuardProvider({ children }: { children: ReactNode }) {
  const unsaved = useRef(new Map<string, string>());
  const [pending, setPending] = useState<{ go: () => void; message: string } | null>(null);

  const setUnsaved = useCallback((key: string, message: string | null) => {
    if (message) unsaved.current.set(key, message);
    else unsaved.current.delete(key);
  }, []);

  const guarded = useCallback((go: () => void) => {
    const [message] = unsaved.current.values();
    if (message) setPending({ go, message });
    else go();
  }, []);

  const value = useMemo(() => ({ setUnsaved, guarded }), [setUnsaved, guarded]);

  return (
    <LeaveGuardContext.Provider value={value}>
      {children}
      <ConfirmDialog
        open={!!pending}
        title="Leave without saving?"
        message={pending?.message ?? ""}
        confirmLabel="Leave without saving"
        onConfirm={async () => {
          const go = pending?.go;
          // The page being left clears its own entry as it unmounts.
          setPending(null);
          go?.();
          return true;
        }}
        onClose={() => setPending(null)}
      />
    </LeaveGuardContext.Provider>
  );
}

/** While `dirty`, leaving through the sidebar asks first, and so does the browser. */
export function useLeaveGuard(key: string, dirty: boolean, message: string) {
  const ctx = useContext(LeaveGuardContext);
  const setUnsaved = ctx?.setUnsaved;
  useEffect(() => {
    if (!dirty) return;
    setUnsaved?.(key, message);
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      setUnsaved?.(key, null);
      window.removeEventListener("beforeunload", warn);
    };
  }, [key, dirty, message, setUnsaved]);
}

/** Runs `go` now, or once the person agrees to leave unsaved changes behind. */
export function useGuardedNavigation(): (go: () => void) => void {
  const ctx = useContext(LeaveGuardContext);
  return ctx?.guarded ?? ((go) => go());
}
